import React from 'react';
import { Check, Edit2, Loader, Trash2, X } from 'react-feather';

import { useVendorDropdown } from '@/Product/hooks/useVendorDropdown';

interface VendorDropdownProps {
  currentVendor: { id: number; vendor: string } | null;
  onVendorChange: (vendor: { id: number; vendor: string }) => void;
}

const VendorDropdown: React.FC<VendorDropdownProps> = ({
  currentVendor,
  onVendorChange,
}) => {
  const {
    isOpen,
    inputValue,
    vendorList,
    editingVendorId,
    editingVendorName,
    showCreateButton,
    searchKeyword,
    isLoadingVendors,
    isCreating,
    isUpdating,
    isDeleting,
    handleInputClick,
    handleInputChange,
    handleVendorSelect,
    handleVendorCreate,
    handleVendorEditStart,
    handleVendorEditComplete,
    handleVendorEditCancel,
    handleVendorDelete,
    setEditingVendorName,
    dropdownRef,
    inputRef,
  } = useVendorDropdown({ currentVendor, onVendorChange });

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative h-10 w-full rounded border border-[#CECECE] px-3 text-sm">
        <input
          ref={inputRef}
          type="text"
          className="h-full w-[90%] border-none outline-none"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onClick={handleInputClick}
          placeholder="Click to add vendor"
        />
      </div>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full rounded border border-[#CECECE] bg-white shadow-lg">
          <div className="max-h-[400px] overflow-y-auto">
            {isLoadingVendors ? (
              <div className="flex items-center justify-center p-4">
                <Loader size={16} className="animate-spin text-[#95699F]" />
                <span className="ml-2 text-sm text-gray-600">
                  Loading vendors...
                </span>
              </div>
            ) : (
              <>
                {/* Vendor 목록 */}
                {vendorList.map((vendor) => (
                  <div
                    key={vendor.id}
                    className="flex items-center justify-between border-b border-gray-100 p-3 last:border-b-0 hover:bg-gray-50"
                  >
                    {editingVendorId === vendor.id ? (
                      // 수정 모드
                      <div className="flex flex-1 items-center gap-2">
                        <input
                          type="text"
                          className="flex-1 rounded border border-[#CECECE] px-2 py-1 text-sm outline-none focus:border-[#95699F]"
                          value={editingVendorName}
                          onChange={(e) => setEditingVendorName(e.target.value)}
                          // eslint-disable-next-line jsx-a11y/no-autofocus
                          autoFocus
                        />
                        <div className="flex gap-1">
                          <button
                            type="button"
                            className="flex h-6 w-6 items-center justify-center rounded text-[#8F689A] hover:text-blue-500"
                            onClick={handleVendorEditComplete}
                            disabled={isUpdating}
                          >
                            {isUpdating ? (
                              <Loader size={12} className="animate-spin" />
                            ) : (
                              <Check size={12} />
                            )}
                          </button>
                          <button
                            type="button"
                            className="flex h-6 w-6 items-center justify-center rounded text-[#666666]"
                            onClick={handleVendorEditCancel}
                            disabled={isUpdating}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      // 일반 모드
                      <>
                        <button
                          type="button"
                          className="flex-1 text-left text-sm font-medium text-[#1D1D1D]"
                          onClick={() => handleVendorSelect(vendor)}
                        >
                          {vendor.vendor}
                        </button>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            className="flex h-6 w-6 items-center justify-center rounded text-[#666666] hover:text-blue-500"
                            onClick={() => handleVendorEditStart(vendor)}
                          >
                            <Edit2 size={12} />
                          </button>
                          <button
                            type="button"
                            className="flex h-6 w-6 items-center justify-center rounded text-[#666666] hover:text-red-500"
                            onClick={() => handleVendorDelete(vendor.id)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? (
                              <Loader size={12} className="animate-spin" />
                            ) : (
                              <Trash2 size={12} />
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {/* 생성 버튼 */}
                {showCreateButton && (
                  <div className="border-t border-gray-200 p-3">
                    <button
                      type="button"
                      className="flex w-full items-center justify-center gap-2 rounded bg-[#95699F] px-3 py-2 text-sm text-white hover:bg-[#B995BE]"
                      onClick={handleVendorCreate}
                      disabled={isCreating}
                    >
                      {isCreating ? (
                        <>
                          <Loader size={14} className="animate-spin" />
                          Creating...
                        </>
                      ) : (
                        `"${searchKeyword}" 생성`
                      )}
                    </button>
                  </div>
                )}

                {/* 검색 결과 없음 */}
                {!isLoadingVendors &&
                  vendorList.length === 0 &&
                  !showCreateButton &&
                  searchKeyword && (
                    <div className="p-4 text-center text-sm text-gray-500">
                      검색 결과가 없습니다.
                    </div>
                  )}

                {/* 기본 상태 (검색어 없음) */}
                {!isLoadingVendors &&
                  vendorList.length === 0 &&
                  !searchKeyword && (
                    <div className="p-4 text-center text-sm text-gray-500">
                      Vendor를 검색하거나 선택하세요.
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorDropdown;
